import { ButtonHTMLAttributes, forwardRef } from 'react'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, disabled, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-[120ms] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed'
    
    const variants = {
      primary: 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]',
      secondary: 'border border-gray-300 bg-transparent text-gray-900 hover:bg-gray-50',
      tertiary: 'bg-transparent text-gray-900 hover:bg-gray-50',
      danger: 'bg-transparent text-red-600 hover:bg-red-50 border border-red-200',
    }
    
    const sizes = {
      sm: 'px-3 py-1.5 text-sm rounded-full',
      md: 'px-4 py-2 text-base rounded-full',
      lg: 'px-6 py-3 text-lg rounded-full',
    }
    
    return (
      <button
        ref={ref}
        className={`
          ${baseStyles}
          ${variants[variant]}
          ${sizes[size]}
          hover:shadow-sm hover:scale-[1.02]
          ${className || ''}
        `}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-current" />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'